import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { DollarSign, Search, Plus, Pencil, Trash2, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TarifAotPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ville: '', tarif_simple: '', tarif_monte_meuble: '' });
    const [saving, setSaving] = useState(false);

    const [toDelete, setToDelete] = useState(null);

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/tarifs-aot`, {
                params: search ? { search } : {},
                withCredentials: true,
            });
            setItems(data);
        } catch {
            toast.error('Erreur chargement tarifs AOT');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ville: '', tarif_simple: '', tarif_monte_meuble: '' });
        setIsFormOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setForm({
            ville: item.ville || '',
            tarif_simple: item.tarif_simple != null ? String(item.tarif_simple) : '',
            tarif_monte_meuble: item.tarif_monte_meuble != null ? String(item.tarif_monte_meuble) : '',
        });
        setIsFormOpen(true);
    };

    const handleSave = async () => {
        const ville = form.ville.trim();
        if (!ville) {
            toast.error('Ville requise');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ville,
                tarif_simple: form.tarif_simple.trim() === '' ? null : form.tarif_simple.trim(),
                tarif_monte_meuble: form.tarif_monte_meuble.trim() === '' ? null : form.tarif_monte_meuble.trim(),
            };
            if (editing) {
                await axios.patch(`${API}/api/tarifs-aot/${editing.id}`, payload, { withCredentials: true });
                toast.success('Tarif mis à jour');
            } else {
                await axios.post(`${API}/api/tarifs-aot`, payload, { withCredentials: true });
                toast.success('Tarif ajouté');
            }
            setIsFormOpen(false);
            fetchItems();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        try {
            await axios.delete(`${API}/api/tarifs-aot/${toDelete.id}`, { withCredentials: true });
            toast.success('Tarif supprimé');
            setToDelete(null);
            fetchItems();
        } catch {
            toast.error('Erreur suppression');
        }
    };

    const fmt = (v) => (v == null || v === '' ? '-' : String(v));

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="tarif-aot-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1
                        className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="tarif-aot-title"
                    >
                        Tarif AOT
                    </h1>
                    <p className="mt-1 text-slate-500">Tarification des autorisations de stationnement par ville</p>
                </div>
                <Button
                    onClick={openCreate}
                    className="bg-[#2e7cd6] hover:bg-blue-700 text-white"
                    data-testid="tarif-aot-add-btn"
                >
                    <Plus size={20} className="mr-2" />
                    Nouvelle ville
                </Button>
            </div>

            {/* Search */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher une ville..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="tarif-aot-search"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <DollarSign size={20} className="text-[#2e7cd6]" />
                        Liste des tarifs ({items.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            {search ? 'Aucune ville ne correspond à la recherche' : 'Aucune ville enregistrée'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="tarifs-aot-table" className="min-w-[600px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Ville</TableHead>
                                        <TableHead className="text-right">Tarif AOT simple</TableHead>
                                        <TableHead className="text-right pr-8">Tarif AOT monte-meuble</TableHead>
                                        <TableHead className="text-right w-28">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id} data-testid={`tarif-aot-row-${item.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <MapPin size={14} className="text-[#2e7cd6]" />
                                                    <span className="font-medium text-slate-900">{item.ville}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-semibold text-slate-700 tabular-nums">
                                                {fmt(item.tarif_simple)}
                                            </TableCell>
                                            <TableCell className="text-right pr-8 font-semibold text-slate-700 tabular-nums">
                                                {fmt(item.tarif_monte_meuble)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 text-slate-500 hover:text-[#2e7cd6]"
                                                        onClick={() => openEdit(item)}
                                                        data-testid={`tarif-aot-edit-${item.id}`}
                                                    >
                                                        <Pencil size={15} />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 text-slate-500 hover:text-red-600"
                                                        onClick={() => setToDelete(item)}
                                                        data-testid={`tarif-aot-delete-${item.id}`}
                                                    >
                                                        <Trash2 size={15} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Form Dialog */}
            <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                <DialogContent className="max-w-md" data-testid="tarif-aot-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Modifier la ville' : 'Nouvelle ville'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Nom de la ville</Label>
                            <Input
                                value={form.ville}
                                onChange={(e) => setForm((p) => ({ ...p, ville: e.target.value }))}
                                placeholder="Ex : Paris"
                                className="mt-1"
                                data-testid="tarif-aot-form-ville"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Tarif AOT simple</Label>
                            <Input
                                value={form.tarif_simple}
                                onChange={(e) => setForm((p) => ({ ...p, tarif_simple: e.target.value }))}
                                placeholder="Ex : Gratuit, Sur devis, 50 €..."
                                className="mt-1"
                                data-testid="tarif-aot-form-simple"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Tarif AOT monte-meuble</Label>
                            <Input
                                value={form.tarif_monte_meuble}
                                onChange={(e) => setForm((p) => ({ ...p, tarif_monte_meuble: e.target.value }))}
                                placeholder="Ex : Gratuit, Sur devis, 120 €..."
                                className="mt-1"
                                data-testid="tarif-aot-form-mm"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsFormOpen(false)}
                            disabled={saving}
                            data-testid="tarif-aot-form-cancel"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#2e7cd6] hover:bg-blue-700 text-white"
                            data-testid="tarif-aot-form-save"
                        >
                            {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
                            {editing ? 'Enregistrer' : 'Ajouter'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <AlertDialog open={!!toDelete} onOpenChange={(open) => { if (!open) setToDelete(null); }}>
                <AlertDialogContent data-testid="tarif-aot-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce tarif ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Le tarif AOT pour la ville <strong>{toDelete?.ville}</strong> sera définitivement supprimé.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
